import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdventures } from '../hooks/useAdventures';
import NewBookingCalendar from '../components/NewBookingCalendar';

const NewBookPage = () => {
  const { adventures, loading: adventuresLoading, error: adventuresError } = useAdventures();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Core booking state
  const [selectedAdventureId, setSelectedAdventureId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  
  // Booking form state
  const [bookingData, setBookingData] = useState({
    groupSize: 1,
    specialRequirements: '',
    customerInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    }
  });

  // UI state
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle payment status from Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/v1/checkout/status/${sessionId}`);
      const data = await response.json();
      
      // Check for paid status (API returns payment_status, not status)
      if (data.payment_status === 'paid') {
        navigate('/booking-success', { 
          state: { 
            bookingReference: data.booking_reference,
            customerEmail: data.customer_email
          }
        });
      } else if (data.status === 'expired' || data.status === 'cancelled') {
        // Handle expired or cancelled sessions
        console.log('Payment session expired or cancelled');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  // Get selected adventure details
  const getSelectedAdventure = () => {
    if (!selectedAdventureId || !adventures.length) return null;
    return adventures.find(a => a.id === selectedAdventureId);
  };

  // Get effective price (with override if applicable)
  const getEffectivePrice = () => {
    const adventure = getSelectedAdventure();
    if (!adventure) return 0;
    
    if (selectedDate && availableDates.length > 0) {
      const dateInfo = availableDates.find(d => d.date === selectedDate);
      if (dateInfo && dateInfo.price_override) {
        return dateInfo.price_override;
      }
    }
    
    return adventure.base_price;
  };

  // Handle adventure selection
  const handleAdventureSelect = (adventureId) => {
    console.log('Adventure selected:', adventureId);
    setSelectedAdventureId(adventureId);
    setSelectedDate(''); // Reset selected date when changing adventure
    setErrors({}); // Clear any errors
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    console.log('Date selected:', date);
    setSelectedDate(date);
    setErrors({});
  };

  // Fetch availability dates when adventure is selected
  useEffect(() => {
    if (selectedAdventureId) {
      fetchAvailabilityDates();
    }
  }, [selectedAdventureId]);

  const fetchAvailabilityDates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/availability/${selectedAdventureId}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data.dates || []);
      }
    } catch (error) {
      console.error('Error fetching availability dates:', error);
      setAvailableDates([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBookingData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBookingData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedAdventureId) {
      newErrors.adventure = 'Please select an adventure';
    }
    
    if (!selectedDate) {
      newErrors.date = 'Please select a date';
    }
    
    if (!bookingData.customerInfo.firstName) {
      newErrors['customerInfo.firstName'] = 'First name is required';
    }
    
    if (!bookingData.customerInfo.lastName) {
      newErrors['customerInfo.lastName'] = 'Last name is required';
    }
    
    if (!bookingData.customerInfo.email) {
      newErrors['customerInfo.email'] = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(bookingData.customerInfo.email)) {
      newErrors['customerInfo.email'] = 'Email is invalid';
    }
    
    if (!bookingData.customerInfo.phone) {
      newErrors['customerInfo.phone'] = 'Phone number is required';
    }

    if (bookingData.groupSize < 1 || bookingData.groupSize > 8) {
      newErrors.groupSize = 'Group size must be between 1 and 8';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle booking submission
  const handleBooking = async () => {
    if (!validateForm()) {
      return;
    }

    const selectedAdventure = getSelectedAdventure();
    if (!selectedAdventure) return;

    setLoadingPayment(true);

    try {
      // Create booking
      const bookingResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adventure_id: selectedAdventureId,
          customer_first_name: bookingData.customerInfo.firstName,
          customer_last_name: bookingData.customerInfo.lastName,
          customer_email: bookingData.customerInfo.email,
          customer_phone: bookingData.customerInfo.phone,
          group_size: bookingData.groupSize,
          selected_date: selectedDate,
          special_requirements: bookingData.specialRequirements,
          host_url: window.location.origin
        }),
      });

      if (!bookingResponse.ok) {
        const errorData = await bookingResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create booking');
      }

      const bookingData_response = await bookingResponse.json();

      // Backend already created Stripe checkout session and returns the URL
      if (bookingData_response.checkout_url) {
        // Redirect to Stripe
        window.location.href = bookingData_response.checkout_url;
      } else {
        throw new Error('No checkout URL received from server');
      }

    } catch (error) {
      console.error('Booking error:', error);
      setErrors({ general: 'Failed to process booking. Please try again.' });
    } finally {
      setLoadingPayment(false);
    }
  };

  if (adventuresLoading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500 mb-4"></div>
          <p className="text-slate-600">Loading adventures...</p>
        </div>
      </div>
    );
  }

  if (adventuresError || !adventures.length) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-red-600 font-medium">Failed to load adventures</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const selectedAdventure = getSelectedAdventure();
  const effectivePrice = getEffectivePrice();
  const totalAmount = effectivePrice * bookingData.groupSize;

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Book Your Adventure</h1>
          <p className="text-slate-600">Choose your adventure and secure your spot today</p>
        </div>

        {errors.general && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{errors.general}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Adventure Selection */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">1. Choose Your Adventure</h2>
              
              {errors.adventure && (
                <div className="mb-4 text-red-600 text-sm">{errors.adventure}</div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {adventures.map((adventure) => (
                  <motion.button
                    key={adventure.id}
                    onClick={() => handleAdventureSelect(adventure.id)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      selectedAdventureId === adventure.id
                        ? 'border-coral-500 bg-coral-50'
                        : 'border-slate-200 hover:border-coral-300 hover:bg-slate-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start">
                      <img 
                        src={adventure.hero_image || adventure.image || '/api/placeholder/80/80'}
                        alt={adventure.title}
                        className="w-16 h-16 rounded-lg object-cover mr-4"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 mb-1">
                          {adventure.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                          {adventure.short_description || adventure.tagline}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-coral-600">
                            ${adventure.base_price}
                          </span>
                          <span className="text-xs text-slate-500">
                            {adventure.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Date Selection */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">2. Select Date</h2>
              
              {errors.date && (
                <div className="mb-4 text-red-600 text-sm">{errors.date}</div>
              )}
              
              <NewBookingCalendar
                adventureId={selectedAdventureId}
                onDateSelect={handleDateSelect}
                selectedDate={selectedDate}
              />
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">3. Your Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={bookingData.customerInfo.firstName}
                    onChange={(e) => handleInputChange('customerInfo.firstName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all ${
                      errors['customerInfo.firstName'] 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-slate-200 focus:border-coral-500 focus:ring-coral-200'
                    } focus:ring-2`}
                    placeholder="Enter your first name"
                  />
                  {errors['customerInfo.firstName'] && (
                    <p className="text-red-600 text-sm mt-1">{errors['customerInfo.firstName']}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={bookingData.customerInfo.lastName}
                    onChange={(e) => handleInputChange('customerInfo.lastName', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all ${
                      errors['customerInfo.lastName'] 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-slate-200 focus:border-coral-500 focus:ring-coral-200'
                    } focus:ring-2`}
                    placeholder="Enter your last name"
                  />
                  {errors['customerInfo.lastName'] && (
                    <p className="text-red-600 text-sm mt-1">{errors['customerInfo.lastName']}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={bookingData.customerInfo.email}
                    onChange={(e) => handleInputChange('customerInfo.email', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all ${
                      errors['customerInfo.email'] 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-slate-200 focus:border-coral-500 focus:ring-coral-200'
                    } focus:ring-2`}
                    placeholder="Enter your email"
                  />
                  {errors['customerInfo.email'] && (
                    <p className="text-red-600 text-sm mt-1">{errors['customerInfo.email']}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={bookingData.customerInfo.phone}
                    onChange={(e) => handleInputChange('customerInfo.phone', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all ${
                      errors['customerInfo.phone'] 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-slate-200 focus:border-coral-500 focus:ring-coral-200'
                    } focus:ring-2`}
                    placeholder="Enter your phone number"
                  />
                  {errors['customerInfo.phone'] && (
                    <p className="text-red-600 text-sm mt-1">{errors['customerInfo.phone']}</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Group Size
                </label>
                <select
                  value={bookingData.groupSize}
                  onChange={(e) => handleInputChange('groupSize', parseInt(e.target.value))}
                  className={`w-full px-4 py-3 rounded-xl border transition-all ${
                    errors.groupSize 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-slate-200 focus:border-coral-500 focus:ring-coral-200'
                  } focus:ring-2`}
                >
                  {[1,2,3,4,5,6,7,8].map(size => (
                    <option key={size} value={size}>
                      {size} {size === 1 ? 'person' : 'people'}
                    </option>
                  ))}
                </select>
                {errors.groupSize && (
                  <p className="text-red-600 text-sm mt-1">{errors.groupSize}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Special Requirements (Optional)
                </label>
                <textarea
                  value={bookingData.specialRequirements}
                  onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all resize-none"
                  placeholder="Any special dietary requirements, medical conditions, or requests..."
                />
              </div>
            </div>
          </div>

          {/* Sidebar - Booking Summary - Only sticky on large screens */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-8 lg:sticky lg:top-8">
              <h3 className="text-xl font-bold text-slate-800 mb-6">Booking Summary</h3>
              
              {selectedAdventure ? (
                <>
                  <div className="mb-6">
                    <img 
                      src={selectedAdventure.hero_image || selectedAdventure.image || '/api/placeholder/200/120'}
                      alt={selectedAdventure.title}
                      className="w-full h-32 rounded-lg object-cover mb-3"
                    />
                    <h4 className="font-semibold text-slate-800">{selectedAdventure.title}</h4>
                    <p className="text-sm text-slate-600 mt-1">{selectedAdventure.duration}</p>
                  </div>

                  <div className="border-t pt-4 space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span>Date:</span>
                      <span className="font-medium">
                        {selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' }) : 'Not selected'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span>Group size:</span>
                      <span className="font-medium">
                        {bookingData.groupSize} {bookingData.groupSize === 1 ? 'person' : 'people'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span>Price per person:</span>
                      <div className="text-right">
                        {selectedDate && availableDates.find(d => d.date === selectedDate)?.price_override ? (
                          <>
                            <span className="line-through text-slate-400 text-sm">
                              ${selectedAdventure.base_price}
                            </span>
                            <span className="font-semibold text-orange-600 ml-2">
                              ${effectivePrice}
                            </span>
                            <div className="text-xs text-orange-600">Special price</div>
                          </>
                        ) : (
                          <span className="font-semibold">${effectivePrice}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="border-t pt-3 flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-coral-600">${totalAmount}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleBooking}
                    disabled={loadingPayment || !selectedAdventureId || !selectedDate}
                    className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all transform ${
                      loadingPayment || !selectedAdventureId || !selectedDate
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-coral-500 hover:bg-coral-600 text-white hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    {loadingPayment ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      'Book Now & Pay'
                    )}
                  </button>

                  <p className="text-xs text-slate-500 text-center mt-3">
                    Secure payment powered by Stripe
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">🤿</div>
                  <p className="text-slate-500">Select an adventure to see pricing</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBookPage;